/** @jsxImportSource @emotion/react */
import React, { useState } from "react";
import Grid from "@material-ui/core/Grid";
import Tooltip from "@material-ui/core/Tooltip";
import { makeStyles } from "@material-ui/core/styles";
import { history } from "../../../Helpers/history";
import { BackButton } from "../../Utils/Buttons/DataExportationButton";
import {
  SidebarContainer,
  SidebarSection,
  SidebarSectionHeader,
  SidebarSectionTitle,
  SidebarItem,
  SidebarItemBadge,
  ChevronIcon,
  MiniSidebarContainer,
  MiniSidebarItem,
  MiniSidebarLabel,
  MiniSidebarDivider,
} from "../Dashboard/ObGynDashboardStyles";

/* ─── Material-UI Styles ──────────────────────────────────── */
const useStyles = makeStyles(() => ({
  item2: {
    borderLeft: "1px solid #c7c6c7",
  },
  pathItem: {
    height: "100vh",
  },
  item: {
    overflow: "auto",
    "&::-webkit-scrollbar": {
      width: "5px",
      height: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#c7c6c7",
      "-webkit-border-radius": "10px",
      borderRadius: "10px",
    },
  },
}));

/* ─── Section Color Config ────────────────────────────────── */
const sectionColors = {
  obstetrics: "#3871B7",
  gynaecology: "#c2185b",
};

/* ─── SVG Icons for Mini Sidebar ──────────────────────────── */
const MiniIcons = {
  triage: () => (
    <svg viewBox="0 0 24 24">
      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z" />
    </svg>
  ),
  notes: () => (
    <svg viewBox="0 0 24 24">
      <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
    </svg>
  ),
  lab: () => (
    <svg viewBox="0 0 24 24">
      <path d="M7 2v2h1v14c0 2.21 1.79 4 4 4s4-1.79 4-4V4h1V2H7zm6 16c0 .55-.45 1-1 1s-1-.45-1-1v-2h2v2zm0-4h-2V4h2v10z" />
    </svg>
  ),
  radiology: () => (
    <svg viewBox="0 0 24 24">
      <path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14zM5 15h14v3H5z" />
    </svg>
  ),
  diagnosis: () => (
    <svg viewBox="0 0 24 24">
      <path d="M20 7l-8-5-8 5v10l8 5 8-5V7zm-8 11.1L7 15V9l5-3.1L17 9v6l-5 3.1z" />
    </svg>
  ),
  prescription: () => (
    <svg viewBox="0 0 24 24">
      <path d="M6 2c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6H6zm1 2h7v5h5v11H6V4zm2 8v2h8v-2H8zm0 4v2h5v-2H8z" />
    </svg>
  ),
  procedure: () => (
    <svg viewBox="0 0 24 24">
      <path d="M20.71 7.04c.39-.39.39-1.04 0-1.41l-2.34-2.34c-.37-.39-1.02-.39-1.41 0l-1.84 1.83 3.75 3.75M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" />
    </svg>
  ),
  admission: () => (
    <svg viewBox="0 0 24 24">
      <path d="M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3 1.34 3 3 3zm12-6h-8v7H3V5H1v15h2v-3h18v3h2V10c0-2.21-1.79-4-4-4z" />
    </svg>
  ),
  history: () => (
    <svg viewBox="0 0 24 24">
      <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z" />
    </svg>
  ),
  treatment: () => (
    <svg viewBox="0 0 24 24">
      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
    </svg>
  ),
};

/**
 * Build the route tree for the OB/GYN left sidebar.
 * Only Obstetrics and Gynaecology sections — clinical actions go in the right mini sidebar.
 */
export const getObGynSidebarRoutes = (
  patient_number,
  visit_id,
  patient_type,
) => {
  const base = (slug) =>
    `/gyn-and-obs-${slug}/${patient_number}/${visit_id}/${patient_type}`;

  return {
    sections: [
      {
        key: "gynaecology",
        title: "Gynaecology",
        color: sectionColors.obstetrics,
        items: [
          {
            text: "GYN Consultation",
            path: base("gynae-consultation"),
            available: true,
          },
          {
            text: "Cervical Cancer Screening",
            path: base("cervical-screening"),
            available: true,
          },
          {
            text: "GBV Screening",
            path: base("gbv-screening"),
            available: true,
          },
          {
            text: "Family Planning",
            path: base("family-planning"),
            available: true,
          },
        ],
      },
      {
        key: "obstetrics",
        title: "Obstetrics",
        color: sectionColors.obstetrics,
        items: [
          {
            text: "ANC Registration & Booking",
            path: base("antenatal-card"),
            available: true,
          },
          {
            text: "ANC Clinical Assessment",
            path: base("anc-assessment"),
            available: true,
          },
          {
            text: "ANC Visit Schedule",
            path: base("anc-schedule"),
            available: true,
          },
          // {
          //   text: "Screening & Investigations",
          //   path: base("anc-investigations"),
          //   available: true,
          // },
          {
            text: "Preventive Treatments",
            path: base("anc-preventive"),
            available: true,
          },
          // {
          //   text: "Birth Preparedness",
          //   path: base("birth-preparedness"),
          //   available: true,
          // },
          {
            text: "eMTCT – HIV in Pregnancy",
            path: base("emtct"),
            available: true,
          },
          // { text: "Labour Ward", path: base("labour-ward"), available: true },
          { text: "Partograph", path: base("partograph"), available: true },
          {
            text: "Delivery & Birth Record",
            path: base("delivery-record"),
            available: true,
          },
          {
            text: "Postnatal Care",
            path: base("postnatal-care"),
            available: true,
          },
        ],
      },
    ],
  };
};

/**
 * Clinical action items for the right mini sidebar.
 */
const getClinicalActions = (patient_number, visit_id, patient_type) => {
  const base = (slug) =>
    `/gyn-and-obs-${slug}/${patient_number}/${visit_id}/${patient_type}`;

  return [
    {
      key: "triage",
      label: "Triage",
      path: base("triage"),
      Icon: MiniIcons.triage,
    },
    // {
    //   key: "notes",
    //   label: "Notes",
    //   path: base("clinical-notes"),
    //   Icon: MiniIcons.notes,
    // },
    { key: "lab", label: "Lab", path: base("lab"), Icon: MiniIcons.lab },
    {
      key: "radiology",
      label: "Radiology",
      path: base("radiology"),
      Icon: MiniIcons.radiology,
    },
    {
      key: "diagnosis",
      label: "Diagnosis",
      path: base("diagnosis"),
      Icon: MiniIcons.diagnosis,
    },
    {
      key: "rx",
      label: "Rx",
      path: base("prescription"),
      Icon: MiniIcons.prescription,
    },
    {
      key: "procedure",
      label: "Procedure",
      path: base("procedure"),
      Icon: MiniIcons.procedure,
    },
    {
      key: "admission",
      label: "Admit",
      path: base("admission"),
      Icon: MiniIcons.admission,
    },
    // { key: "divider", label: "", path: null, Icon: null },
    // {
    //   key: "history",
    //   label: "History",
    //   path: base("medical-history"),
    //   Icon: MiniIcons.history,
    // },
    // {
    //   key: "treatment",
    //   label: "Summary",
    //   path: base("treatment-details"),
    //   Icon: MiniIcons.treatment,
    // },
  ];
};

/**
 * ObGynSidebar — Three-column layout for the OB/GYN module.
 *
 * Layout:
 *  [Left Sidebar (2)]  |  [Content (9)]  |  [Right Mini Sidebar (1)]
 *
 * Left: Obstetrics + Gynaecology nav
 * Right: Clinical action icon buttons (Triage, Notes, Lab, Radiology, etc.)
 */
const ObGynSidebar = ({ children, patient_number, visit_id, patient_type }) => {
  const classes = useStyles();
  const routes = getObGynSidebarRoutes(patient_number, visit_id, patient_type);
  const clinicalActions = getClinicalActions(
    patient_number,
    visit_id,
    patient_type,
  );

  const [expanded, setExpanded] = useState({
    obstetrics: true,
    gynaecology: true,
  });

  const toggleSection = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const currentPath = history.location.pathname;

  return (
    <div>
      <div className="general-ledger-header mb-0">
        <BackButton text="waiting list" to="/gyn-and-obs-waiting-list" />
      </div>
      <Grid container spacing={0}>
        {/* ─── Left Sidebar: OBS + GYN ─── */}
        <Grid
          item
          xs={3}
          md={2}
          sm={2}
          lg={2}
          classes={{ item: `${classes.item} ${classes.pathItem}` }}
        >
          <SidebarContainer>
            {routes.sections.map((section) => (
              <SidebarSection key={section.key}>
                <SidebarSectionHeader
                  expanded={expanded[section.key]}
                  onClick={() => toggleSection(section.key)}
                >
                  <SidebarSectionTitle color={section.color}>
                    {section.title}
                  </SidebarSectionTitle>
                  <ChevronIcon expanded={expanded[section.key]}>▾</ChevronIcon>
                </SidebarSectionHeader>

                {expanded[section.key] &&
                  section.items.map((item, idx) => (
                    <SidebarItem
                      key={idx}
                      active={item.available && currentPath === item.path}
                      disabled={!item.available}
                      onClick={() => {
                        if (item.available && item.path) {
                          history.push(item.path);
                        }
                      }}
                    >
                      {item.text}
                      {!item.available && (
                        <SidebarItemBadge variant="default">
                          Soon
                        </SidebarItemBadge>
                      )}
                    </SidebarItem>
                  ))}
              </SidebarSection>
            ))}
          </SidebarContainer>
        </Grid>

        {/* ─── Content Area ─── */}
        <Grid
          item
          sm={9}
          xs={8}
          md={9}
          lg={9}
          classes={{ item: classes.item2 }}
        >
          {children}
        </Grid>

        {/* ─── Right Mini Sidebar: Clinical Actions ─── */}
        <Grid item xs={1} md={1} sm={1} lg={1}>
          <MiniSidebarContainer>
            {clinicalActions.map((action) => {
              if (action.key === "divider") {
                return <MiniSidebarDivider key="divider" />;
              }
              const isActive = currentPath === action.path;
              return (
                <Tooltip
                  key={action.key}
                  title={action.label}
                  placement="left"
                  arrow
                >
                  <MiniSidebarItem
                    active={isActive}
                    onClick={() => history.push(action.path)}
                  >
                    <action.Icon />
                    <MiniSidebarLabel active={isActive}>
                      {action.label}
                    </MiniSidebarLabel>
                  </MiniSidebarItem>
                </Tooltip>
              );
            })}
          </MiniSidebarContainer>
        </Grid>
      </Grid>
    </div>
  );
};

export default ObGynSidebar;
